import {
  gridSizeMdLg12, texto, gridSizeMdLg6,
  fnMostrarTipoTarjeta,
  gridSizeMdLg9,
  gridSizeMdLg3
} from "@/shared"
import {
  TabTextLabel, CustomAutocomplete,
  CustomTextField, CustomAutocompleteArrString,
  CustomTextFieldNumberComponente,
  SampleDatePicker, CustomNumberTextField, SampleCheckbox,
} from "@/shared/components/ui"
import { TiposTextFieldNumberEnum } from "@/shared/components/ui/CustomTextFieldNumberComponente"
import {
  Cooperativa, EntidadesFinancieras,
  Plan, TipoInstalacion,
  TipoSolicitudesEnum
} from "@/shared/interfaces"
import {
  IPaymentMethodV2,
  TipoPagoV2Enum,
  TIPO_PAGO_V2_OPTIONS,
} from "@/shared/interfaces/erp/system-settings"
import { Edit } from "@mui/icons-material"
import { Alert, Grid, Typography } from "@mui/material"
import dayjs from "dayjs"
import { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from "react-hook-form"
import { useTypeInstalation } from "@/store/erp/system-settings/type-instalation"
import { usePlanStore } from "@/store/erp/system-settings/plan.store"
import { TooltipIconEdit } from "@/erp/sale"
import { IPreventaPymesForm } from "@/shared/interfaces/erp/ventas_pymes/preventa_pymes.interface"
import { CustomAlertCard } from "@/shared/components/ui/Alerts"

interface TabServicioPreventaProps {
  editSupervisor: boolean
  form: UseFormReturn<IPreventaPymesForm, any, IPreventaPymesForm>
  dataCooperativa: Cooperativa[] | undefined
  isLoadingCooperativa: boolean
  handleValidarTarjeta: (numeroTarjeta: string) => void
  tipoTarjeta: string | null
  handlePlanes: () => Plan[]
  fields: FieldArrayWithId<IPreventaPymesForm, "items", "id">[]
  append: UseFieldArrayAppend<IPreventaPymesForm, "items">
  remove: UseFieldArrayRemove
  enlaceValorInstalacion: (plan: Plan, es_pago_total: boolean, index: number) => void
  valorInstalacionCompleto: (pago: boolean) => void
  handleValidarTipoVenta: string
  // V2 - 3 niveles de pago
  handleTipoPagoV2Change: (tipoPago: TipoPagoV2Enum) => Promise<void>
  handleEntidadV2Change: (entidadCodigo: string) => Promise<void>
  handleFormaPagoV2Change: (formaPago: IPaymentMethodV2) => Promise<void>
  entidadesV2Filtradas: EntidadesFinancieras[]
  isLoadingEntidadesV2: boolean
  formasPagoV2Filtradas: IPaymentMethodV2[]
  isLoadingFormasPagoV2: boolean
}

export default function TabServicioPreventaPymes({
  editSupervisor,
  form,
  dataCooperativa,
  isLoadingCooperativa,
  handleValidarTarjeta,
  tipoTarjeta,
  handlePlanes,
  fields,
  enlaceValorInstalacion,
  valorInstalacionCompleto,
  handleValidarTipoVenta,
  // V2 - 3 niveles de pago
  handleTipoPagoV2Change,
  handleEntidadV2Change,
  handleFormaPagoV2Change,
  entidadesV2Filtradas,
  isLoadingEntidadesV2,
  formasPagoV2Filtradas,
  isLoadingFormasPagoV2,
}: TabServicioPreventaProps) {

  const {
    control,
    watch,
    formState: { errors }
  } = form

  // Watch para hacer reactivos los campos condicionales
  const tipoPagoV2 = watch('tipo_pago_v2');
  const entidadFinanciera = watch('entidad_financiera');
  const formaPagoV2 = watch('forma_pago_v2');

  const typInstalation = useTypeInstalation((s) => s.typInstalation);
  const isLoadingTypeInstalation = useTypeInstalation(
    (s) => s.isLoadingTypeInstalation
  );

  const isLoadingPlans = usePlanStore((s) => s.isLoadingPlans);

  return (
    <Grid container spacing={3}>
      <Grid item {...gridSizeMdLg12} display={"flex"}>
        <TabTextLabel text={texto.pagos} mt={0} pb={2} pl={0} pt={0} />
        {editSupervisor && <TooltipIconEdit />}
      </Grid>

      {/* ============ NIVEL 1: Tipo de Pago (DÉBITO/CRÉDITO/RECAUDACIÓN) ============ */}
      <CustomAutocompleteArrString
        size={gridSizeMdLg6}
        label="Tipo de Pago *"
        name="tipo_pago_v2"
        options={TIPO_PAGO_V2_OPTIONS}
        defaultValue={form.getValues().tipo_pago_v2 || ""}
        isLoadingData={false}
        control={control}
        error={errors.tipo_pago_v2}
        helperText={errors.tipo_pago_v2?.message}
        onChangeValue={(e) => handleTipoPagoV2Change(e as TipoPagoV2Enum)}
      />

      {/* ============ NIVEL 2: Entidad Bancaria (filtrada por tipo de pago) - No aplica para RECAUDACIÓN ni TRANSFERENCIA ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && tipoPagoV2 !== TipoPagoV2Enum.TRANSFERENCIA && (
        <CustomAutocomplete<EntidadesFinancieras>
          key={`entidad-${tipoPagoV2}`}
          textFieldKey={`entidad-field-${tipoPagoV2}`}
          size={gridSizeMdLg6}
          label="Entidad Bancaria *"
          name="entidad_financiera"
          options={entidadesV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().entidad_financiera || ""}
          optionLabelForEdit={form.getValues().entidad_financiera || ""}
          isLoadingData={isLoadingEntidadesV2}
          control={control}
          error={errors.entidad_financiera}
          helperText={errors.entidad_financiera?.message}
          onChangeValue={(e) => handleEntidadV2Change(e)}
        />
      )}

      {/* ============ NIVEL 3: Forma de Pago V2 (filtrada por entidad + tipo) - No aplica para RECAUDACIÓN ni TRANSFERENCIA ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && tipoPagoV2 !== TipoPagoV2Enum.TRANSFERENCIA && entidadFinanciera && (
        <CustomAutocomplete<IPaymentMethodV2>
          key={`forma-pago-${tipoPagoV2}-${entidadFinanciera}`}
          textFieldKey={`forma-pago-field-${tipoPagoV2}-${entidadFinanciera}`}
          size={gridSizeMdLg6}
          label="Forma de Pago *"
          name="forma_pago_v2"
          options={formasPagoV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().forma_pago_v2 || ""}
          optionLabelForEdit={formasPagoV2Filtradas?.find(fp => fp.codigo === form.getValues().forma_pago_v2)?.nombre || ""}
          isLoadingData={isLoadingFormasPagoV2}
          control={control}
          error={errors.forma_pago_v2}
          helperText={errors.forma_pago_v2?.message}
          onChangeRawValue={(e) => handleFormaPagoV2Change(e)}
        />
      )}

      <CustomTextField
        size={gridSizeMdLg6}
        disabled={true}
        name="tipo_contrato"
        label={texto.tipo_contrato}
        control={form.control}
        defaultValue={form.getValues().tipo_contrato || ""}
        error={errors.tipo_contrato}
        helperText={errors.tipo_contrato?.message}
      />

      {/* ============ Campos condicionales según tipo de pago V2 - DÉBITO ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.DEBITO && formaPagoV2 && (
        <>
          {!isLoadingCooperativa &&
            dataCooperativa?.length != 0 && (
              <CustomAutocomplete<Cooperativa>
                size={gridSizeMdLg6}
                label={texto.cooperativa}
                name="cooperativa"
                options={dataCooperativa || []}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={form.getValues().cooperativa || ""}
                isLoadingData={isLoadingCooperativa}
                control={control}
                error={errors.cooperativa}
                helperText={errors.cooperativa?.message}
              />
            )}

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            size={gridSizeMdLg12}
            label={texto.numero_cuenta}
            error={errors.numero_cuenta}
            helperText={errors.numero_cuenta?.message}
            control={form.control}
            name="numero_cuenta"
            defaultValue={form.getValues().numero_cuenta}
            max={25}
          />
        </>
      )}

      {/* ============ Campos condicionales según tipo de pago V2 - CRÉDITO ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.CREDITO && formaPagoV2 && (
        <>
          <SampleDatePicker
            format="YYYY-MM"
            views={["year", "month"]}
            label="Fecha Caducidad *"
            name="tarjeta_fecha_caducidad"
            minDate={dayjs(new Date())}
            control={form.control}
            defaultValue={
              form.getValues().tarjeta_fecha_caducidad ?? ""
            }
            error={errors.tarjeta_fecha_caducidad}
            helperText={errors.tarjeta_fecha_caducidad?.message}
            size={gridSizeMdLg6}
          />

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            startAdornment={fnMostrarTipoTarjeta(tipoTarjeta) ? (
              <img
                src={fnMostrarTipoTarjeta(tipoTarjeta)}
                alt="imge-tarjeta"
                width={"50px"}
              />
            ) : null}
            size={gridSizeMdLg6}
            label={texto.ingresar_num_tarjeta}
            error={errors.numero_tarjeta}
            helperText={errors.numero_tarjeta?.message}
            control={form.control}
            name="numero_tarjeta"
            defaultValue={form.getValues().numero_tarjeta ?? ""}
            max={24}
            onChangeValue={(e) => handleValidarTarjeta(e)}
          />
        </>
      )}

      <Grid item {...gridSizeMdLg12} display={"flex"}>
        <TabTextLabel
          text={texto.instalacion}
          mt={0}
          pb={0}
          pl={0}
          pt={2}
        />
        {editSupervisor && <TooltipIconEdit />}
      </Grid>

      <CustomAutocomplete<TipoInstalacion>
        size={gridSizeMdLg6}
        label={texto.tipo_instalacion}
        name="tipo_instalacion"
        // options
        options={typInstalation || []}
        valueKey="nombre" // name is the fk
        actualValueKey="codigo"
        defaultValue={form.getValues().tipo_instalacion || ""}
        isLoadingData={isLoadingTypeInstalation}
        // validation
        control={control}
        error={errors.tipo_instalacion}
        helperText={errors.tipo_instalacion?.message}
        onChangeValue={(e) => {
          form.setValue('tipo_instalacion', e)
        }}
      />
      <Grid item xs={12}>
        <TabTextLabel
          text={`Plan`}
          mt={0}
          pb={1}
          pl={0}
          pt={0}
        />
      </Grid>

      {
        (formaPagoV2 || tipoPagoV2 === TipoPagoV2Enum.RECAUDACION || tipoPagoV2 === TipoPagoV2Enum.TRANSFERENCIA) ? (
          <>
            {
              fields.map((fields, index) => {
                const itemErrors = errors.items?.[index];

                return (
                  <Grid key={fields.id} item container spacing={3}>
                    <Grid item xs={12}>
                      <TabTextLabel
                        text={`Plan ${index + 1} - ${form.getValues(`items.${index}.nombre`)}`}
                        mt={0}
                        pb={1}
                        pl={0}
                        pt={0}
                      />
                    </Grid>


                    <CustomAutocomplete<Plan>
                      size={gridSizeMdLg6}
                      label={texto.plan}
                      name={`items.${index}.plan`}
                      options={handlePlanes()}
                      valueKey="nombre"
                      actualValueKey="codigo"
                      defaultValue={fields.plan || ""}
                      isLoadingData={isLoadingPlans}
                      control={control}
                      error={itemErrors?.plan}
                      helperText={itemErrors?.plan?.message}
                      onChangeRawValue={async (e) => {

                        enlaceValorInstalacion(
                          e,
                          false, index
                        );
                        //handleSelectPlan(e)
                      }}
                    />

                    <CustomTextField
                      endAdornmentInput={
                        editSupervisor ? <Edit color="action" /> : null
                      }
                      disabled={true}
                      name={`items.${index}.valor_plan`}
                      label={'Valor del Plan'}
                      control={form.control}
                      defaultValue={
                        fields.valor_plan || ""
                      }
                      error={errors.valor_plan}
                      helperText={errors.valor_plan?.message}
                      size={gridSizeMdLg6}
                    />

                    {/*Cuando es canje o evento el valor de instalación es cero*/}
                    {
                      handleValidarTipoVenta != TipoSolicitudesEnum.PYMES && (
                        <>
                          <CustomAlertCard
                            alertColor="info"
                            alertTitle="Información"
                            alertMessage={`${handleValidarTipoVenta} con valor de instalación en 0.`}
                          />
                        </>
                      )
                    }

                    {
                      handleValidarTipoVenta == TipoSolicitudesEnum.PYMES && (
                        <>
                          <CustomNumberTextField
                            disabled={true}
                            name={`items.${index}.valor_instalacion`} // valor sin iva
                            label={texto.valor_instalacion_sin_iva}
                            control={form.control}
                            defaultValue={fields.valor_instalacion || ""}
                            error={errors.valor_instalacion}
                            helperText={errors.valor_instalacion?.message}
                            size={gridSizeMdLg6}
                            step={0.01}
                          />

                          <CustomTextField
                            endAdornmentInput={
                              editSupervisor ? <Edit color="action" /> : null
                            }
                            disabled={true}
                            name={`items.${index}.valor_instalacion_total`}
                            label={texto.valor_total_instalacion}
                            control={form.control}
                            defaultValue={
                              fields.valor_instalacion_total || ""
                            }
                            error={errors.valor_instalacion_total}
                            helperText={errors.valor_instalacion_total?.message}
                            size={gridSizeMdLg6}
                          />

                        </>
                      )
                    }


                  </Grid>
                )
              })
            }

            <Grid item xs={12}>
              <TabTextLabel
                text={`Contrato`}
                mt={0}
                pb={1}
                pl={0}
                pt={0}
              />
            </Grid>

            {
              handleValidarTipoVenta == TipoSolicitudesEnum.PYMES && (
                <>
                  <Grid container item {...gridSizeMdLg6}>
                    <CustomNumberTextField
                      label="Tiempo de Contrato"
                      name="tiempo_contrato"
                      control={form.control}
                      error={undefined}
                      helperText={undefined}
                      defaultValue={form.getValues()?.tiempo_contrato}
                      size={gridSizeMdLg9}
                      onChangeValue={(value) => {
                        if (value != 36) {
                          form.setValue('es_pago_total', true)
                          valorInstalacionCompleto(true);
                        } else {
                          form.setValue('es_pago_total', false)
                          valorInstalacionCompleto(false);
                        }
                      }}
                    />
                    <Grid container item {...gridSizeMdLg3} alignContent={"center"} justifyContent={"center"}>
                      <Typography variant="subtitle1">
                        MESES
                      </Typography>
                    </Grid>
                  </Grid>

                  <SampleCheckbox
                    sizeCheck="medium"
                    label="El cliente ha indicado que prefiere pagar el valor completo de la instalación."
                    name="es_pago_total"
                    control={form.control}
                    defaultValue={form.getValues().es_pago_total || false}
                    size={gridSizeMdLg6}
                    onChangeValue={(e) => {
                      // Si marca el check, tiempo_contrato = 24, si desmarca = 36
                      if (e) {
                        form.setValue('tiempo_contrato', 24);
                      } else {
                        form.setValue('tiempo_contrato', 36);
                      }
                      valorInstalacionCompleto(e ?? false);
                    }}
                  />
                </>
              )
            }


            {
              handleValidarTipoVenta != TipoSolicitudesEnum.PYMES && (
                <>
                  <SampleDatePicker
                    minDate={new Date}
                    label="Fecha Inicio Contrato"
                    control={form.control}
                    error={errors.fecha_inicio_contrato}
                    helperText={errors.fecha_inicio_contrato?.message}
                    name="fecha_inicio_contrato"
                    textFieldKey="fecha_instalacion"
                    defaultValue={null}
                    size={gridSizeMdLg6}
                  />

                  <SampleDatePicker
                    minDate={new Date}
                    label="Fecha Fin Contrato"
                    control={form.control}
                    error={errors.fecha_fin_contrato}
                    helperText={errors.fecha_fin_contrato?.message}
                    name="fecha_fin_contrato"
                    textFieldKey="fecha_instalacion"
                    defaultValue={null}
                    size={gridSizeMdLg6}
                  />
                </>
              )
            }

          </>
        ) :
          (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="info">
                Selecciona una forma de pago para ver los planes disponibles.
              </Alert>
            </Grid>
          )
      }




    </Grid>
  )

}